import { AppData } from '@lightningjs/sdk';
import {
  ErrorType,
  PlaybackEvent,
  SeekType,
  ViewContext,
} from 'types/analytics';
import { EpgChannel, LiveEventStream, Media, Video } from 'types/api/media';
import { PageId } from 'types/pageId';
import { Ad } from 'types/player';
import { ListItemContext } from 'types/events';
import { secondsToMilliseconds } from 'support/timeUtils';
import { ErrorCode } from './reportingServicePage';
import {
  InteractiveAdCompleteType,
  InteractiveAdType,
} from 'components/pages/playback/interactive-ads/InteractiveAdUtils';
import { VideoPayload } from 'types/analytics/mParticle';

export type AdType = 'pre-roll' | 'mid-roll' | 'post-roll';
type VideoReportingPayload = { mParticle: VideoPayload };

type ProgressTime = {
  relativeTime: number; // either current content time without ads, or ad time within ad pod
  rawTime: number; // time from video player (content + ads)
};

// handling the argument type this way allows us to add additional args for specific events
type ReportPlaybackArgs =
  | [event: PlaybackEvent, progressTime: ProgressTime]
  | [event: PlaybackEvent, progressTime: ProgressTime, type: undefined]
  | [event: PlaybackEvent.START, progressTime: ProgressTime]
  | [
      event: PlaybackEvent.END,
      progressTime: ProgressTime,
      state: {
        hasEnded: boolean;
        isAdPlaying: boolean;
        isEpgUpdate?: boolean;
      },
    ]
  | [
      event: PlaybackEvent.SEEK_START,
      progressTime: ProgressTime,
      type: SeekType,
    ]
  | [
      event: PlaybackEvent.BUFFER_END,
      progressTime: ProgressTime,
      resumePlayback: boolean,
    ]
  | [event: PlaybackEvent.AD_START, progressTime: ProgressTime, ad: Ad]
  | [
      event: PlaybackEvent.AD_POD_END,
      progressTime: ProgressTime,
      skipped: boolean,
    ]
  | [
      event: PlaybackEvent.CREDITS_REACHED,
      progressTime: ProgressTime,
      firstCredits: boolean,
    ]
  | [
      event: PlaybackEvent.LIVE_EVENT_END,
      progressTime: ProgressTime,
      payload?: VideoReportingPayload,
    ];

const getAdType = (ad: Ad): AdType => {
  let type: AdType;

  const podIndex = ad.getAdPodInfo().getPodIndex();
  if (podIndex === 0) {
    type = 'pre-roll';
  } else if (podIndex === -1) {
    type = 'post-roll';
  } else {
    type = 'mid-roll';
  }

  return type;
};

const logVideoEvent = (message: string) => {
  AppData?.log.debug(message);
};

const reportStart = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportStart');

  window.analytics.comscore.reportStart(progressTime.rawTime);
  window.analytics.conviva.video.reportStart();
  window.analytics.mParticle.video.reportStart(progressTime.relativeTime);
  window.analytics.nielsen.reportStart(progressTime.relativeTime);
  window.analytics.brightcove.reportVideoView(
    progressTime.relativeTime,
    'START',
  );
};

const reportResume = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportResume');

  window.analytics.comscore.reportResume(progressTime.relativeTime);
  window.analytics.conviva.video.reportResume();
  window.analytics.nielsen.reportResume();
  window.analytics.brightcove.reportVideoView(
    progressTime.relativeTime,
    'RESUME',
  );
};

const reportPause = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportPause');

  window.analytics.comscore.reportPause();
  window.analytics.conviva.video.reportPause();
  window.analytics.nielsen.reportPaused(progressTime.relativeTime);
};

const reportSeekPause = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportSeekPause');

  window.analytics.comscore.reportSeekStart(progressTime.relativeTime);
};

const reportBeforeSeek = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportBeforeSeek');

  window.analytics.comscore.reportSeekStart(progressTime.relativeTime);
  window.analytics.conviva.video.reportSeekStart(progressTime.relativeTime);
  window.analytics.nielsen.reportBeforeSeek(progressTime.relativeTime);
};

const reportSeekStart = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportSeekStart');

  window.analytics.comscore.reportSeekStart(progressTime.relativeTime);
};

const reportSeekEnd = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportSeekEnd');

  window.analytics.comscore.reportSeekEnd(progressTime.relativeTime);
  window.analytics.conviva.video.reportSeekEnd();
  window.analytics.nielsen.reportSeekEnd(progressTime.relativeTime);
};

const reportBufferStart = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportBufferStart');

  window.analytics.comscore.reportBufferStart(progressTime.relativeTime);
  window.analytics.conviva.video.reportBufferStart();
};

const reportBufferEnd = (
  progressTime: ProgressTime,
  resumePlayback?: boolean,
) => {
  logVideoEvent('[reporting service video] reportBufferEnd');

  window.analytics.conviva.video.reportBufferEnd(resumePlayback ?? false);
  window.analytics.comscore.reportBufferEnd(
    progressTime.relativeTime,
    resumePlayback ?? false,
  );
};

const reportAdPodStart = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportAdPodStart');

  window.analytics.nielsen.reportAdPodStart(progressTime.relativeTime);
  window.analytics.conviva.video.reportAdPodStart();
};

const reportAdStart = (progressTime: ProgressTime, ad?: Ad) => {
  logVideoEvent('[reporting service video] reportAdStart');

  const type = ad ? getAdType(ad) : 'pre-roll'; // pre-roll as a fallback, this should never occur
  const duration = secondsToMilliseconds(ad!.getDuration() ?? 0);

  window.analytics.comscore.reportAdStart(type, duration);
  window.analytics.conviva.video.reportAdStart(ad, type);
  window.analytics.nielsen.reportAdStart();
};

const reportAdEnd = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportAdEnd');

  window.analytics.conviva.video.reportAdEnd();
  window.analytics.nielsen.reportAdEnd(progressTime.relativeTime);
};

const reportAdPodEnd = (progressTime: ProgressTime, skipped?: boolean) => {
  logVideoEvent('[reporting service video] reportAdPodEnd');

  window.analytics.comscore.reportAdPodEnd();
  window.analytics.conviva.video.reportAdPodEnd(!!skipped);
  window.analytics.nielsen.reportAdPodEnd(progressTime.relativeTime);
};

const reportCreditsReached = (
  progressTime: ProgressTime,
  firstCredits: boolean,
) => {
  logVideoEvent(
    `[reporting service video] reportCreditsReached, isFirstCredits: ${firstCredits}`,
  );

  window.analytics.mParticle.video.reportCreditsReached(
    progressTime.relativeTime,
    firstCredits,
  );
};

const reportLiveEventEnd = (
  progressTime: ProgressTime,
  payload?: VideoReportingPayload,
) => {
  logVideoEvent('[reporting service video] reportLiveEventEnd');

  window.analytics.mParticle.video.reportLiveEventEnd(
    progressTime.relativeTime,
    payload?.mParticle,
  );
};

const reportEnd = (
  progressTime: ProgressTime,
  hasEnded: boolean,
  isAdPlaying: boolean,
  isEpgUpdate: boolean,
) => {
  logVideoEvent('[reporting service video] reportEnd');

  window.analytics.conviva.video.reportPlaybackEnded(isAdPlaying);
  window.analytics.comscore.reportEnd();
  window.analytics.nielsen.reportEnd(progressTime.relativeTime, hasEnded);

  if (isEpgUpdate) {
    window.analytics.mParticle.video.reportEnd(
      progressTime.relativeTime,
      hasEnded,
    );
  }
  window.analytics.brightcove.reportEnd();
};

const reportComplete = (progressTime: ProgressTime) => {
  logVideoEvent('[reporting service video] reportComplete');

  window.analytics.nielsen.reportComplete(progressTime.relativeTime);
};

export const reportPlayback = (
  ...[event, progressTime, additionalArg]: ReportPlaybackArgs
) => {
  const progressTimeInMs: ProgressTime = {
    relativeTime: progressTime.relativeTime * 1000,
    rawTime: progressTime.rawTime * 1000,
  };

  switch (event) {
    case PlaybackEvent.START:
      reportStart(progressTimeInMs);
      break;
    case PlaybackEvent.PAUSE:
      reportPause(progressTimeInMs);
      break;
    case PlaybackEvent.SEEK_PAUSE:
      reportSeekPause(progressTimeInMs);
      break;
    case PlaybackEvent.RESUME:
      reportResume(progressTimeInMs);
      break;
    case PlaybackEvent.SEEK_START:
      reportSeekStart(progressTimeInMs);
      break;
    case PlaybackEvent.BEFORE_SEEK:
      reportBeforeSeek(progressTimeInMs);
      break;
    case PlaybackEvent.SEEK_END:
      reportSeekEnd(progressTimeInMs);
      break;
    case PlaybackEvent.BUFFER_START:
      reportBufferStart(progressTimeInMs);
      break;
    case PlaybackEvent.BUFFER_END:
      reportBufferEnd(progressTimeInMs, additionalArg);
      break;
    case PlaybackEvent.AD_POD_START:
      reportAdPodStart(progressTimeInMs);
      break;
    case PlaybackEvent.AD_START:
      reportAdStart(progressTimeInMs, additionalArg);
      break;
    case PlaybackEvent.AD_END:
      reportAdEnd(progressTimeInMs);
      break;
    case PlaybackEvent.AD_POD_END:
      reportAdPodEnd(progressTimeInMs, additionalArg);
      break;
    case PlaybackEvent.LIVE_EVENT_END:
      reportLiveEventEnd(progressTimeInMs, additionalArg);
      break;
    case PlaybackEvent.CREDITS_REACHED:
      reportCreditsReached(progressTimeInMs, !!additionalArg);
      break;
    case PlaybackEvent.END:
      reportEnd(
        progressTimeInMs,
        !!additionalArg?.hasEnded,
        !!additionalArg?.isAdPlaying,
        !!additionalArg?.isEpgUpdate,
      );
      break;
    case PlaybackEvent.COMPLETE:
      reportComplete(progressTimeInMs);
      break;
    default:
      break;
  }
};

export const reportHeartbeat = (relativeTime: number) => {
  const relativeTimeInMs = relativeTime * 1000;

  window.analytics.nielsen.handleHeartbeat(relativeTimeInMs);
  window.analytics.conviva.video.handleHeartbeat(relativeTimeInMs);
};

export const reportContentMetadata = (args: {
  mediaContent: Video | LiveEventStream | EpgChannel;
  fromPageId: PageId | null;
  viewContext: ViewContext | null;
  listItemContext?: ListItemContext;
}) => {
  const { mediaContent, fromPageId, viewContext, listItemContext } = args;
  const videoSessionId = getNewVideoSessionId();

  window.analytics.conviva.video.reportContentPlayback(
    mediaContent,
    viewContext,
    videoSessionId,
  );
  window.analytics.comscore.setContentMetadata(mediaContent);
  window.analytics.nielsen.setContentMetadata(mediaContent);
  window.analytics.mParticle.video.setContentPayload(
    mediaContent,
    fromPageId,
    videoSessionId,
    listItemContext,
  );
  window.analytics.brightcove.setContentMetadata(mediaContent);
};

export const reportAdMetadata = (ad: Ad) => {
  const type: AdType = getAdType(ad);

  window.analytics.conviva.video.reportAdMetadata(ad, type);
  window.analytics.nielsen.setAdMetadata(ad, type);
};

export const reportBitrate = (bitrate: number) => {
  window.analytics.conviva.video.reportBitrate(bitrate);
};

export const reportClosedCaptionState = (enabled: boolean) => {
  window.analytics.mParticle.video.setClosedCaptionPayload(enabled);
};

export const reportPlayerLoading = () => {
  window.analytics.conviva.video.setVideoPlayer('HTML5');
  window.analytics.conviva.video.setAdListener('HTML5');
};

export const reportStreamLoaded = (url: string) => {
  window.analytics.conviva.video.setStreamUrl(url);
  window.analytics.conviva.video.reportCdn(url);
};

export const reportPlaybackError = (
  isFatal: boolean,
  errorType: ErrorType,
  message: string,
) => {
  if (isFatal) {
    window.analytics.conviva.video.reportPlaybackFailed(message);
  } else {
    window.analytics.conviva.video.reportPlaybackWarning(message);
  }

  window.analytics.mParticle.video.reportPlaybackError(errorType, message);
  AppData?.device.reportError({
    error: new Error(message),
    userFacing: isFatal,
    code: ErrorCode.c33,
  });
};

export const reportBackgrounded = () => {
  window.analytics.conviva.video.reportBackgrounded();
  window.analytics.comscore.reportBackgrounded();
};

export const reportForegrounded = () => {
  window.analytics.conviva.video.reportForegrounded();
  window.analytics.comscore.reportForegrounded();
};

export const reportStartOver = () => {
  window.analytics.mParticle.video.reportStart(0);
};

export const reportMidPoint = (relativeTime: number) => {
  window.analytics.mParticle.video.reportMidPoint(relativeTime);
};

export const reportPlayRecommendation = (
  type: 'clicked' | 'autoPlay',
  content: Media,
) => {
  window.analytics.mParticle.video.reportPlayRecommendation(type, content);
};

export const reportEpgProgramSwitch = (
  channel: EpgChannel,
  viewContext: ViewContext | null,
) => {
  // Only Conviva metadata is updated
  window.analytics.conviva.video.setContentInfo(
    channel,
    viewContext,
    getNewVideoSessionId(),
  );
  window.analytics.comscore.reportEpgProgramSwitch(channel);
};

export const reportInteractiveAdSlotStart = (adType?: InteractiveAdType) => {
  window.analytics.mParticle.video.reportInteractiveAdSlotStart(adType);
};

export const reportInteractiveAdSlotComplete = (adType?: InteractiveAdType) => {
  window.analytics.mParticle.video.reportInteractiveAdSlotComplete(adType);
};

export const reportInteractiveAdExit = (
  isCredit: boolean,
  adCompleteType = InteractiveAdCompleteType.COMPLETE,
  adType?: InteractiveAdType,
) => {
  const exitType = getInteractiveAdExitType(isCredit, adCompleteType);

  window.analytics.mParticle.video.reportInteractiveAdExit(exitType, adType);
  window.analytics.conviva.video.reportInteractiveAdExit(exitType);

  // Google IMA doesn't properly report the ad-slot completion due to player/event reloading
  window.analytics.conviva.video.reportAdEnd();

  // CW ask to include certain flows as errors
  if (
    adCompleteType === InteractiveAdCompleteType.ERROR ||
    adCompleteType === InteractiveAdCompleteType.NO_ADS_AVAILABLE
  ) {
    reportPlaybackError(
      false,
      ErrorType.INTERACTIVE_AD_ERROR,
      'Interactive Ad Error',
    );
  }
};

export const releaseAnalytics = () => {
  if (window.analytics) {
    window.analytics.conviva?.video.release();
    window.analytics.nielsen?.endSession?.();
  }
};

export const getNewVideoSessionId = (): string => {
  return `${Math.floor(Date.now() / 1000)}`;
};

export const getVideoReportingPayload = (): VideoReportingPayload => {
  return { mParticle: window.analytics.mParticle.video.getMParticlePayload() };
};

const getInteractiveAdExitType = (
  isCredit: boolean,
  adCompleteType: InteractiveAdCompleteType,
) => {
  switch (adCompleteType) {
    case InteractiveAdCompleteType.ERROR:
      return 'Error';
    case InteractiveAdCompleteType.NO_ADS_AVAILABLE:
      return 'No Ads';
    case InteractiveAdCompleteType.COMPLETE:
    default:
      return isCredit ? 'Close' : 'Skip';
  }
};
